// src/components/FeedbackButton.jsx
// Persistent feedback button (bottom-right corner).
// One click opens a modal — user types feedback, submits to Supabase feedback table.

import { useState } from 'react'
import { supabase } from '../lib/supabaseClient'

export default function FeedbackButton({ userId, currentPage }) {
  const [open,    setOpen]    = useState(false)
  const [message, setMessage] = useState('')
  const [status,  setStatus]  = useState('idle') // 'idle' | 'sending' | 'sent' | 'error'

  async function handleSubmit(e) {
    e.preventDefault()
    if (!message.trim() || status === 'sending') return

    setStatus('sending')

    if (!supabase) {
      // No Supabase — just acknowledge and close
      setStatus('sent')
      setMessage('')
      setTimeout(() => { setOpen(false); setStatus('idle') }, 1800)
      return
    }

    const { error } = await supabase
      .from('feedback')
      .insert({ user_id: userId ?? null, message: message.trim(), current_page: currentPage ?? null })

    if (error) {
      setStatus('error')
      return
    }

    setStatus('sent')
    setMessage('')
    setTimeout(() => {
      setOpen(false)
      setStatus('idle')
    }, 1800)
  }

  function handleClose() {
    setOpen(false)
    setMessage('')
    setStatus('idle')
  }

  return (
    <>
      {/* Floating trigger button */}
      <button
        onClick={() => setOpen(true)}
        title="Share feedback"
        style={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 8000,
          background: '#111111',
          border: '1px solid #1c2333',
          borderRadius: 24,
          padding: '9px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          cursor: 'pointer',
          fontFamily: 'var(--font-mono, JetBrains Mono, monospace)',
          fontSize: 11,
          fontWeight: 700,
          color: '#8b949e',
          letterSpacing: '.06em',
          transition: 'border-color .15s, color .15s',
          boxShadow: '0 2px 12px rgba(0,0,0,.5)',
        }}
        onMouseEnter={e => {
          e.currentTarget.style.borderColor = 'var(--accent, #3ddc84)'
          e.currentTarget.style.color = 'var(--accent, #3ddc84)'
        }}
        onMouseLeave={e => {
          e.currentTarget.style.borderColor = '#1c2333'
          e.currentTarget.style.color = '#8b949e'
        }}
      >
        {/* bubble icon */}
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
        FEEDBACK
      </button>

      {/* Modal backdrop */}
      {open && (
        <div
          onClick={handleClose}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 8500,
            background: 'rgba(0,0,0,.55)',
          }}
        />
      )}

      {/* Modal */}
      {open && (
        <div style={{
          position: 'fixed',
          bottom: 80,
          right: 24,
          zIndex: 8600,
          background: '#111111',
          border: '1px solid #1c2333',
          borderTop: '2px solid var(--accent, #3ddc84)',
          borderRadius: 12,
          width: 340,
          padding: '24px 24px 20px',
          boxShadow: '0 8px 32px rgba(0,0,0,.7)',
        }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
            <div>
              <div style={{
                fontFamily: 'var(--font-mono, JetBrains Mono, monospace)',
                fontSize: 9,
                color: '#8b949e',
                letterSpacing: '.16em',
                textTransform: 'uppercase',
                marginBottom: 4,
              }}>
                Feedback
              </div>
              <div style={{
                fontFamily: 'var(--font-display, Syne, sans-serif)',
                fontSize: 16,
                fontWeight: 700,
                color: '#c9d1d9',
              }}>
                What's confusing or broken?
              </div>
            </div>

            <button
              onClick={handleClose}
              style={{
                background: 'none',
                border: 'none',
                color: '#8b949e',
                cursor: 'pointer',
                padding: 2,
                lineHeight: 1,
                fontSize: 18,
              }}
            >
              ×
            </button>
          </div>

          {status === 'sent' ? (
            <div style={{
              fontFamily: 'var(--font-body, IBM Plex Sans, sans-serif)',
              fontSize: 13,
              color: 'var(--accent, #3ddc84)',
              padding: '16px 0',
              textAlign: 'center',
            }}>
              Thanks — feedback received.
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <textarea
                value={message}
                onChange={e => { setMessage(e.target.value); setStatus('idle') }}
                placeholder="Tell us what's wrong or confusing…"
                rows={4}
                style={{
                  width: '100%',
                  background: '#0d1117',
                  border: `1px solid ${status === 'error' ? '#ff6b35' : '#1c2333'}`,
                  borderRadius: 8,
                  padding: '10px 12px',
                  fontFamily: 'var(--font-body, IBM Plex Sans, sans-serif)',
                  fontSize: 13,
                  color: '#c9d1d9',
                  resize: 'vertical',
                  outline: 'none',
                  boxSizing: 'border-box',
                  lineHeight: 1.6,
                  transition: 'border-color .15s',
                }}
                onFocus={e => { if (status !== 'error') e.currentTarget.style.borderColor = 'var(--accent, #3ddc84)' }}
                onBlur={e => { if (status !== 'error') e.currentTarget.style.borderColor = '#1c2333' }}
                autoFocus
              />

              {status === 'error' && (
                <div style={{
                  fontFamily: 'var(--font-mono, JetBrains Mono, monospace)',
                  fontSize: 10,
                  color: '#ff6b35',
                  marginTop: 6,
                  letterSpacing: '.04em',
                }}>
                  Failed to send. Try again.
                </div>
              )}

              <button
                type="submit"
                disabled={!message.trim() || status === 'sending'}
                style={{
                  width: '100%',
                  marginTop: 14,
                  background: message.trim() && status !== 'sending' ? 'var(--accent, #3ddc84)' : '#1c2333',
                  border: 'none',
                  borderRadius: 8,
                  padding: '11px 0',
                  fontFamily: 'var(--font-mono, JetBrains Mono, monospace)',
                  fontSize: 11,
                  fontWeight: 700,
                  color: message.trim() && status !== 'sending' ? '#0a0a0a' : '#8b949e',
                  letterSpacing: '.08em',
                  cursor: message.trim() && status !== 'sending' ? 'pointer' : 'default',
                  transition: 'all .15s',
                }}
              >
                {status === 'sending' ? 'SENDING...' : 'SEND FEEDBACK →'}
              </button>
            </form>
          )}
        </div>
      )}
    </>
  )
}
